// Subida de logo desde Administración · Empresa (handoff §2, T-17p9):
// el mismo cable en tres pasos que features/admin/logoUpload.ts —
// presign en core-api, PUT directo al presignado sin Authorization,
// commit— sobre las rutas de autogestión (/organizations/me/…), que no
// llevan id de organización. Sin React: el drawer es dueño del estado,
// este módulo del cable.
//
// Lo que se acepta lo fija el backend (api-types/admin.ts): PNG, JPG o
// WEBP de hasta 2 MB — se reusan las constantes y mensajes de
// features/admin/logoUpload.ts para no tener dos verdades. El handoff
// pide además un mínimo de 400 px de ancho; eso se mide aquí antes de
// subir nada (createImageBitmap, o <img> donde no exista) porque el
// servidor no decodifica imágenes (ADR-0033). SVG queda fuera: media-svc
// no lo firma.
//
// Cada fallo sale como Error con un mensaje es-CL listo para mostrar.

import {
  LOGO_INVALID_FILE_MESSAGE,
  LOGO_PUT_FAILED_MESSAGE,
  LOGO_SESSION_EXPIRED_MESSAGE,
  LOGO_MAX_BYTES,
  validateLogoFile,
} from "../../admin/logoUpload";
import { ApiError } from "../../../lib/api/error";
import { padToSquare } from "../../../lib/media/padToSquare";
import { empresaApi } from "./api";

export { LOGO_ACCEPT_ATTR, LOGO_MAX_BYTES } from "../../admin/logoUpload";

/** Ancho mínimo del handoff: «mínimo 400 px de ancho». */
export const LOGO_MIN_WIDTH = 400;

export const LOGO_TOO_SMALL_MESSAGE = `El logo es muy pequeño: necesita al menos ${LOGO_MIN_WIDTH} px de ancho.`;

export type LogoTarget = { kind: "org" } | { kind: "brand"; brandId: string };

/** Ancho y alto del archivo, sin subirlo. Lanza si el navegador no lo decodifica. */
export async function readImageSize(file: File): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    try {
      return { width: bitmap.width, height: bitmap.height };
    } finally {
      bitmap.close();
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("La imagen no se pudo leer."));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Tipo, peso y ancho mínimo, antes de cualquier viaje al servidor.
 * Devuelve el motivo de rechazo o null si el archivo sirve.
 */
export async function inspectLogoFile(file: File): Promise<string | null> {
  const invalid = validateLogoFile(file);
  if (invalid) return invalid;
  let size: { width: number; height: number };
  try {
    size = await readImageSize(file);
  } catch {
    return LOGO_INVALID_FILE_MESSAGE;
  }
  if (size.width < LOGO_MIN_WIDTH) return LOGO_TOO_SMALL_MESSAGE;
  return null;
}

function isAuthFailure(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

export async function uploadLogoFile(target: LogoTarget, file: File): Promise<{ logoUrl: string }> {
  const invalid = await inspectLogoFile(file);
  if (invalid) throw new Error(invalid);

  // Cuadrado con margen blanco, como el panel /admin: el presign firma
  // contentType + contentLength, así que ambos salen del blob acolchado.
  const { blob, mimeType } = await padToSquare(file);
  if (blob.size > LOGO_MAX_BYTES) throw new Error(LOGO_INVALID_FILE_MESSAGE);

  try {
    const presignBody = { contentType: mimeType, contentLength: blob.size };
    const presign =
      target.kind === "org"
        ? await empresaApi.requestOrgLogoUploadUrl(presignBody)
        : await empresaApi.requestBrandLogoUploadUrl(target.brandId, presignBody);

    let putOk = false;
    try {
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: blob,
        headers: presign.requiredHeaders,
      });
      putOk = putRes.ok;
    } catch {
      putOk = false;
    }
    if (!putOk) throw new Error(LOGO_PUT_FAILED_MESSAGE);

    if (target.kind === "org") {
      const res = await empresaApi.commitOrgLogo({ objectKey: presign.objectKey });
      return { logoUrl: res.organization.logoUrl };
    }
    const res = await empresaApi.commitBrandLogo(target.brandId, { objectKey: presign.objectKey });
    return { logoUrl: res.brand.logoUrl ?? presign.publicUrl };
  } catch (err) {
    if (isAuthFailure(err)) throw new Error(LOGO_SESSION_EXPIRED_MESSAGE);
    throw err;
  }
}
